import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_IMAGE_MEGABYTES = MAX_IMAGE_BYTES / (1024 * 1024);
/** Extension → media type, so the picker filter and the validation cannot drift apart. */
const IMAGE_TYPES: Readonly<Record<string, string>> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg' };
export const ACCEPTED_IMAGE_EXTENSIONS = Object.keys(IMAGE_TYPES);
/** Mirrors the backend's FeedbackAttachments.MAX_COUNT; the server rejects anything above it. */
const MAX_ATTACHMENTS = 1;

type UseFeedbackAttachmentsParams = Readonly<{ isOpen: boolean; isSubmitting: boolean }>;
type UseFeedbackAttachmentsResult = {
  attachments: File[];
  attachmentError: string;
  attachLabel: string;
  attachmentHint: string;
  attachmentPrivacy: string;
  attachmentErrorRef: RefObject<HTMLDivElement>;
  attachButtonRef: RefObject<HTMLButtonElement>;
  handleFilesSelected: (files: File[]) => void;
  handleRemoveAttachment: () => void;
  isAttachDisabled: boolean;
  resetAttachments: () => void;
  showAttachmentError: (message: string) => boolean;
};

/** Some drag sources report an empty `file.type`; the extension then decides, as the picker's filter does. */
function isAllowedImage(file: File): boolean {
  if (file.type) return Object.values(IMAGE_TYPES).includes(file.type);
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return extension in IMAGE_TYPES;
}

function useFeedbackAttachments({ isOpen, isSubmitting }: UseFeedbackAttachmentsParams): UseFeedbackAttachmentsResult {
  const { t } = useTranslation('chatbot');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [errorKey, setErrorKey] = useState('');
  const [shouldRestoreFocus, setShouldRestoreFocus] = useState(false);
  const attachmentErrorRef = useRef<HTMLDivElement>(null);
  const attachButtonRef = useRef<HTMLButtonElement>(null);

  function resetAttachments(): void {
    setAttachments([]);
    setErrorKey('');
  }

  useEffect(() => {
    if (isOpen) resetAttachments();
  }, [isOpen]);

  useEffect(() => {
    if (!shouldRestoreFocus) return;
    attachButtonRef.current?.focus();
    setShouldRestoreFocus(false);
  }, [shouldRestoreFocus]);

  useEffect(() => {
    if (errorKey && !isSubmitting) attachmentErrorRef.current?.focus();
  }, [errorKey, isSubmitting]);

  function handleFilesSelected(files: File[]): void {
    if (isSubmitting || files.length === 0) return;
    if (files.length + attachments.length > MAX_ATTACHMENTS) {
      setErrorKey('feedback_attachment_limit');
      return;
    }
    if (!files.every(isAllowedImage)) {
      setErrorKey('feedback_attachment_invalid_type');
      return;
    }
    if (files.some((file) => file.size > MAX_IMAGE_BYTES)) {
      setErrorKey('feedback_attachment_too_large');
      return;
    }
    setAttachments((previous) => [...previous, ...files]);
    setErrorKey('');
  }

  function handleRemoveAttachment(): void {
    if (isSubmitting) return;
    resetAttachments();
    setShouldRestoreFocus(true);
  }

  function showAttachmentError(message: string): boolean {
    const errorKeys: Record<string, string> = {
      FEEDBACK_ATTACHMENT_TOO_LARGE: 'feedback_attachment_too_large',
      FEEDBACK_ATTACHMENT_INVALID_TYPE: 'feedback_attachment_invalid_type',
      FEEDBACK_ATTACHMENT_LIMIT_EXCEEDED: 'feedback_attachment_limit',
    };
    const key = errorKeys[message];
    if (!key) return false;
    setErrorKey(key);
    return true;
  }

  const limits = { count: MAX_ATTACHMENTS, size: MAX_IMAGE_MEGABYTES };

  return {
    attachments,
    attachmentError: errorKey ? t(`chatbot:${errorKey}`, limits) : '',
    attachLabel: t('chatbot:feedback_attach_image'),
    attachmentHint: t('chatbot:feedback_attachment_hint', limits),
    attachmentPrivacy: t('chatbot:feedback_attachment_privacy'),
    attachmentErrorRef,
    attachButtonRef,
    handleFilesSelected,
    handleRemoveAttachment,
    isAttachDisabled: isSubmitting || attachments.length >= MAX_ATTACHMENTS,
    resetAttachments,
    showAttachmentError,
  };
}

export default useFeedbackAttachments;
